import Vue from 'vue'
import Vuex from 'vuex'
import notifier from './modules/notifier'
import specification from './modules/specification'
import messages from './modules/messages'
import { setI18nLanguage } from '@/services/i18n-setup'
import { api, setCustomerId } from '@/services/ApiService'
import router from '@/router'

Vue.use(Vuex);

const store = new Vuex.Store({
  modules: {
    notifier,
    messages,
    specification
  },
  state: {
    user: null,
    primaryCustomerId: localStorage.getItem('tpa:primary-customer-id') ?? null,
    appVersion: process.env.VUE_APP_VERSION ?? 'unknown version',
    loading: false,
    loadingToggleShowOnlyRelevantContent: false,
    availableLocales: [
      {
        text: 'English',
        value: 'en'
      },
      {
        text: 'Dansk',
        value: 'da'
      }
    ]
  },
  mutations: {
    setUser (state, payload) {
      state.user = payload;
      console.log('User has been committed')
    },
    setLoading (state, value) {
      state.loading = value;
    },
    setOnlyShowRelevantContent (state, value) {
      state.user.showOnlyRelevantContent = value;
    }
  },
  getters: {
    currentUser: state => state.user,
    currentCustomer: state => state.primaryCustomerId ? state.user?.customers?.find(x => x.id === state.primaryCustomerId) : null,
    userHasMultipleCustomers: state => state.user?.customers?.length > 1,
    userId: state => state.user.id,
    userOrganizationName: (state) => {
      return state.user.organizationTitle;
    },
    userHasAcceptedTerms: (state) => !!state.user.termsAcceptedOnDate,
    showOnlyRelevantContent: state => {
      return state.user.showOnlyRelevantContent;
    },
    isInternal: state => state.user?.organizationKind === 'Internal',
    isSuperAdmin: state => state.user?.isSuperAdmin,
    permissions: state => state.user?.permissions ?? [],
    hasAnyPermission: (state) => (testPermissions) => {
      if (!state.user?.permissions.length) return false

      const result = testPermissions.some(permission => {
        const userPermissions = state.user.permissions.map(x => String(x).toLowerCase())
        return userPermissions.includes(permission.toLowerCase())
      })

      return result
    }
  },
  actions: {
    setPrimaryCustomerId({ state, dispatch }, payload) {
      state.primaryCustomerId = payload;
      localStorage.setItem('tpa:primary-customer-id', payload);
      setCustomerId(payload);
      dispatch('setLocale')
    },
    setLocale({ state, getters }) {
      const lang = state.user?.preferredLocale ?? getters.currentCustomer?.defaultLocale ?? 'en';

      setI18nLanguage(lang)
    },
    async loadUser ({ commit, dispatch }) {
      try {
        const { data } = await api.get('users/me');

        if (!data.success) {
          throw 'user_not_found';
        }

        if (data.model.customers?.length === 1) {
          dispatch('setPrimaryCustomerId', data.model.customers[0].id)
        }

        commit('setUser', data.model);
        dispatch('setLocale');
      } catch (error) {
        if (error.isAxiosError) {
          console.error(error.toJSON())
        } else {
          console.error(error)
        }

        if (error.response) {
          const { data, status } = error.response;

          if (typeof data.error === 'string' && data.error !== '') {
            router.push({ path: '/error', query: { code: data.error, status: status } })
          }
        }

        else if (error.message.includes('Network Error')) {
          router.push({ path: '/error', query: { code: 'network_error' } })
        }

        else {
          router.push({ path: '/error' })
        }
      } finally {
        commit('setLoading', false);
      }
    },
    async toggleShowOnlyRelevantContent ({ commit, state, getters }) {
      state.loadingToggleShowOnlyRelevantContent = true;

      try {
        await api.patch(`users/edit/onlyshowrelevantcontent?show=${!getters.showOnlyRelevantContent}`);
        commit('setOnlyShowRelevantContent', !getters.showOnlyRelevantContent);
      } catch (error) {
        console.error(error);
      } finally {
        state.loadingToggleShowOnlyRelevantContent = false;
      }
    },
    logError(context, { level, transaction, message, error = null }) {
      try {
        this._vm.Sentry.withScope((scope) => {
          console.log(typeof level, typeof transaction, message, error);
          if (level) {
            scope.setLevel(level);
          }

          if (transaction) {
            scope.setTransactionName(transaction);
          }

          if (message) {
            this._vm.Sentry.captureException(new Error(message));
          } else if (error) {
            this._vm.Sentry.captureException(error)
          } else {
            this._vm.Sentry.captureException();
          }
        });
      } catch (err) {
        console.error(err);
      }
    },

    clearPrimaryCustomer({ state }) {
      state.primaryCustomerId = null;
      localStorage.removeItem('tpa:primary-customer-id');
    },
    async createInvoice({ commit }, invoice) {
      try {
        const response = await api.post('invoice', invoice);

        const { success, successResponse, errorDetails, errors } = response.data;

        if (!success) {
          throw errorDetails ?? errors;
        }

        // Set invoice URL if it exists
        const invoiceId = successResponse?.soap?.currentInvoiceHandle?.id ?? null;
        const invoiceNumber = successResponse?.draftInvoiceNumber;
        const url = invoiceId ? 'https://secure.e-conomic.com/sales/invoicing/invoices/' + invoiceId : null;

        this._vm.$notifier.showSuccess({
          message: `Fakturakladde ${invoiceNumber} blev oprettet i e-conomic`,
          url
        });

        return url;
      } catch (error) {
        if (Array.isArray(error)) {
          for (const x of error) {
            if (typeof x === 'string' && x.includes('Part number') && x.includes('not found')) {
              const index = invoice.lines.findIndex(line => x.includes(line.partNumber))
              invoice.lines[index].errors.push(x)
            }

            else {
              this._vm.$notifier.showError({ message: error[0], timeout: 0 });
            }
          }
        }

        else if (typeof error === 'object' && error.errors?.lines?.items?.length) {
          error.errors.lines.items.forEach(x => {
            invoice.lines[x.arrayIndex].errors = x.product.errors.map(e => e.errorMessage);
          });

          this._vm.$notifier.showError({ timeout: 0 });
          throw invoice;
        }

        else {
          this._vm.$notifier.showError({ timeout: 0 });
        }

        console.error(error);
      }
    }
  }
})

export default store;
